import type { ReactNode } from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { tokens } from '../../theme/tokens';
import { AppText } from './AppText';

type AppButtonVariant = 'primary' | 'secondary' | 'ghost';
export type AppButtonTone = 'brand' | 'success' | 'warning' | 'danger';
export type AppButtonSize = 'sm' | 'md' | 'lg';

type AppButtonProps = {
  label: string;
  onPress?: () => void;
  variant?: AppButtonVariant;
  tone?: AppButtonTone;
  size?: AppButtonSize;
  disabled?: boolean;
  loading?: boolean;
  leftSlot?: ReactNode;
  style?: StyleProp<ViewStyle>;
};

export function AppButton({
  label,
  onPress,
  variant = 'primary',
  tone = 'brand',
  size = 'md',
  disabled = false,
  loading = false,
  leftSlot,
  style,
}: AppButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <Pressable
      accessibilityRole="button"
      disabled={isDisabled}
      onPress={onPress}
      style={({ pressed }) => [
        styles.base,
        styles[size],
        styles[variant],
        variant === 'primary' ? styles[`${tone}Tone`] : null,
        pressed && !isDisabled ? styles.pressed : null,
        isDisabled ? styles.disabled : null,
        style,
      ]}
    >
      {loading ? <ActivityIndicator color={tokens.colors.white} /> : leftSlot}
      <AppText
        variant="body"
        weight="semibold"
        tone={variant === 'primary' ? 'default' : tone === 'brand' ? 'primary' : tone === 'danger' ? 'danger' : tone === 'success' ? 'success' : 'warning'}
        style={variant === 'primary' ? styles.primaryLabel : null}
      >
        {label}
      </AppText>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    borderRadius: tokens.component.button.radius,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: tokens.component.button.iconGap,
    paddingHorizontal: tokens.spacing.lg,
  },
  sm: {
    minHeight: tokens.component.button.heightSm,
    paddingHorizontal: tokens.spacing.md,
  },
  md: {
    minHeight: tokens.component.button.heightMd,
  },
  lg: {
    minHeight: tokens.component.button.heightLg,
    paddingHorizontal: tokens.spacing.xl,
  },
  primary: {
    backgroundColor: tokens.colors.primary,
  },
  secondary: {
    backgroundColor: tokens.colors.surfaceMuted,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
  },
  ghost: {
    backgroundColor: 'transparent',
  },
  brandTone: {
    backgroundColor: tokens.colors.primary,
  },
  successTone: {
    backgroundColor: tokens.colors.success,
  },
  warningTone: {
    backgroundColor: tokens.colors.warning,
  },
  dangerTone: {
    backgroundColor: tokens.colors.danger,
  },
  pressed: {
    opacity: 0.86,
  },
  disabled: {
    opacity: 0.56,
  },
  primaryLabel: {
    color: tokens.colors.white,
  },
});
